import { tableStyles } from 'css/tableStyles';
import React, { useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import { CentsToDollar, DATE_FORMATS, USER_ROLES, formatAddress, formatDate, getPageLimit, showError, updatePageLimit } from 'utils';
import moment from 'moment';
import styles from './index.module.scss';
import ActionButton from 'components/ActionButton';
import Currency from 'components/Currency/Currency';
import CustomLink from 'components/TableLink';
import { fetchOrders } from 'services';
import qs from 'query-string';
import UsersDropdown from 'components/UsersDropdown';
import { salesPersonOrdersSummary } from 'services/api/salesChannel';
import DatePicker from 'react-datepicker';
import RenderInfo from 'components/RenderInfo';
import SalesPersonSummary from './summary';
import Export from './export';

let filters: { [p: string]: any; page: number } = {
  page: 0,
  startDate: moment().subtract(30, 'days').toISOString(),
  endDate: moment().toISOString(),
};


const SalesPersonSummaryReport = props => {
  const [data, setData] = useState([]) as any;
  const [startDate, setStartDate] = useState<string | null>(moment().subtract(30, 'days').toISOString());
  const [endDate, setEndDate] = useState<string | null>(moment().toISOString());
  const [loading, setLoading] = useState<boolean>(false);
  const [salesPersonId, setSalesPersonId] = useState<string>('');
  const [summary, setSummary] = useState([]) as any;

  const salesPersonOrdersSummaryFn = async () => {
    const { startDate, endDate, salesPersonId } = filters;
    try {
      const res = await salesPersonOrdersSummary({ 
        salesPersonId, 
        startDate: moment(startDate).format(DATE_FORMATS.YYYY_MM_DD), 
        endDate: moment(endDate).format(DATE_FORMATS.YYYY_MM_DD), 
      });
      setSummary(res);
    } catch (e) {
      showError(e);
    }
  };
  
  const fetchOrdersFn = async () => {
    const { page, ...rest } = filters;
    try {
      const filter = {
        ...rest,
      };
      const response = await fetchOrders({
        page,
        limit: getPageLimit(),
        filter,
      });
      setData(response);
    } catch (e) {
      showError(e);
    }
  };

  const fetchData = async () => {
    if (!salesPersonId) {
      showError('Please select sales person');
      return;
    }
    try {
      setLoading(true);
      await Promise.all([
        salesPersonOrdersSummaryFn(),
        fetchOrdersFn(),
      ]);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const filterToQueryString = () => {
    return qs.stringify({ ...filters });
  };

  const updateQueryString = () => {
    const qs = filterToQueryString();
    props.history.push({
      pathname: props.location.pathname,
      search: qs,
    });
  };

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    updateQueryString();
    fetchData();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const onFilterSubmit = () => {
    filters = { startDate, endDate, page: 0, salesPersonId };
    updateQueryString();
    fetchData();
  };

  const columns = useMemo(() => [
    {
      name: 'OrderID',
      selector: 'objectId',
      sortable: false,
      width: '120px',
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          text={row.objectId}
          linkTo={`/orders/${row.objectId}`}
        />
      ),
    },
    {
      name: 'Status',
      selector: 'status',
      sortable: false,
      width: '120px',
    },
    {
      name: 'Ordered At',
      selector: 'createdAt',
      sortable: false,
      format: (row) => formatDate(row.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
      width: '170px',
    },
    {
      name: 'Total',
      selector: 'total',
      sortable: false,
      // eslint-disable-next-line react/display-name
      format: (row) => <Currency amount={row.total / 100} default="-" />,
      width: '120px',
    },
    {
      name: 'Cost',
      selector: 'cost',
      sortable: false,
      // eslint-disable-next-line react/display-name
      format: (row) => <Currency amount={row.cost} default="-" />,
      width: '120px',
    },
    {
      name:'Waiting For Payment',
      selector:'waitingForPayment',
      format :(row)=>  row.waitingForPayment?.toString() ?? '-',
    },
    {
      name: 'Refunded Amount',
      selector: 'refundedAmount',
      sortable: false,
      // eslint-disable-next-line react/display-name
      format: (row) => row.refundedAmount ? (<Currency amount={row.refundedAmount} />) : '-',
      width: '120px',
    },
    {
      name: 'Purchaser Info',
      selector: 'purchaser',
      sortable: false,
      width: '250px',
      // eslint-disable-next-line react/display-name
      cell: (row) => <div style={{ height: 'auto' }}>
        <div style={{ fontSize: '16px' }}>{(row.purchaser.full_name ? row.purchaser.full_name : row.purchaser.username)}</div>
        <div style={{ fontSize: '12px', color: '#8F8F8F' }}>{formatAddress(row.addressObject)}</div>
      </div>,
    },
    {
      name: 'Account Info',
      selector: 'purchaser.account',
      width: '180px',
      // eslint-disable-next-line react/display-name
      cell: (row) => row.purchaser?.account ? (
        <div style={{ height: 'auto' }}>
          <div style={{ fontSize: '12px', color: '#8F8F8F' }}>{row.purchaser.account.objectId}</div>
          <div style={{ fontSize: '16px' }}>{(row.purchaser.account.name || '')}</div>
        </div>
      ): '-',
    },
    {
      name: 'Notes',
      selector: 'notes',
      sortable: false,
      format: row => row.notes + row.notes + row.notes + row.notes + row.notes + row.notes + row.notes,
      width: '220px',
      // eslint-disable-next-line react/display-name
      cell: (row) => <div style={{ height: 'auto' }} onClick={() => window.location.assign(`/orders/${row.objectId}`)}>
        {row.notes ? row.notes : '-'}
      </div>,
    },
  ], []);

  return (
    <div>
      <div className={styles['header']}>
        <div className={styles['heading']}>Sales Person Summary</div>
      </div>
      <div className={styles['filter-container']}>
        <div className={''}>
          <div className={styles['label']}>Start Date</div>
          <div className={''}>
            <DatePicker
              className={'form-control'}
              disabled={loading}
              selected={startDate ? moment(startDate).toDate() : null}
              onChange={(e: any) => setStartDate(formatDate(e, DATE_FORMATS.YYYY_MM_DD))}
            />
          </div>
        </div>
        <div className={''}>
          <div className={styles['label']}>End Date</div>
          <div className={''}>
            <DatePicker
              className={'form-control'}
              disabled={loading}
              selected={endDate ? moment(endDate).toDate() : null}
              onChange={(e: any) => setEndDate(formatDate(moment(e), DATE_FORMATS.YYYY_MM_DD))}
            />
          </div>
        </div>
        <div style={{ width: '300px' }}>
          <div className={styles['label']}>Sales Person</div>
          <UsersDropdown 
            value={salesPersonId} 
            onChange={setSalesPersonId} 
            title="Sales Person" 
            role={USER_ROLES.SALES_PERSON} 
            disabled={loading} 
          />
        </div>
        <Export endDate={endDate} startDate={startDate} salesPersonId={salesPersonId} />
        <ActionButton
          text={'Submit'}
          active={!loading && !!(endDate && startDate && salesPersonId)}
          isLoading={loading}
          onClick={onFilterSubmit}
          customClass={'ml-4 my-auto'}
        />
      </div>
      <SalesPersonSummary summary={summary} loading={loading} />
      <div className='mt-3'>
        <DataTable
          noHeader
          data={data?.items}
          columns={columns}
          customStyles={tableStyles}
          highlightOnHover={true}
          keyField="objectId"
          pagination={true}
          paginationServer={true}
          paginationPerPage={getPageLimit()}
          paginationDefaultPage={(filters.page || 0) + 1}
          paginationTotalRows={data.total}
          paginationRowsPerPageOptions={[50, 100, 200]}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default SalesPersonSummaryReport;